/* eslint-disable react-refresh/only-export-components */
import { useTranslation } from "react-i18next"
import { createBrowserRouter } from "react-router-dom"

import { DashboardLayout } from "@/layouts/dashboard-layout"
import { DeliveryFormDetailPage } from "@/features/delivery-forms/delivery-form-detail-page"
import { LoginPage } from "@/features/auth/login-page"
import { ContractDetailPage } from "@/features/contracts/contract-detail-page"
import { ContractEditPage } from "@/features/contracts/contract-edit-page"
import { ContractNewPage } from "@/features/contracts/contract-new-page"
import { ContractsPage } from "@/features/contracts/contracts-page"
import { CooperativeDetailPage } from "@/features/cooperatives/cooperative-detail-page"
import { CooperativesPage } from "@/features/cooperatives/cooperatives-page"
import { DashboardPage } from "@/features/dashboard/dashboard-page"
import { IntakeDetailPage } from "@/features/intake/intake-detail-page"
import { IntakePage } from "@/features/intake/intake-page"
import { LotDetailPage } from "@/features/lots/lot-detail-page"
import { LotsPage } from "@/features/lots/lots-page"
import { MillFloorPage } from "@/features/mill-floor/mill-floor-page"
import { UsinageDetailPage } from "@/features/milling/usinage-detail-page"
import { OrderDetailPage } from "@/features/orders/order-detail-page"
import { OrderEditPage } from "@/features/orders/order-edit-page"
import { OrderNewPage } from "@/features/orders/order-new-page"
import { OrdersPage } from "@/features/orders/orders-page"
import { PublicSharePage } from "@/features/transit/public-share-page"
import { TransitDetailPage } from "@/features/transit/transit-detail-page"
import { TransitOrdersPage } from "@/features/transit/transit-orders-page"
import { TransitTraceabilityPage } from "@/features/transit/traceability-page"
import { ProtectedRoute } from "@/routes/protected-route"
import { PublicRoute } from "@/routes/public-route"
import { paths } from "@/routes/paths"

function Placeholder({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation(["nav", "common"])
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-2xl font-semibold tracking-tight">{t(titleKey)}</h1>
      <p className="text-sm text-muted-foreground">
        {t("comingSoon", { ns: "common" })}
      </p>
    </div>
  )
}

function NotFound() {
  const { t } = useTranslation("common")
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-2xl font-semibold tracking-tight">{t("notFound")}</h1>
    </div>
  )
}

export const router = createBrowserRouter([
  // Public, no-auth routes — the share-code in the URL is the only
  // capability, gated server-side by a 4-digit PIN.
  { path: "/share/ot/:code", element: <PublicSharePage /> },
  {
    element: <PublicRoute />,
    children: [{ path: paths.login, element: <LoginPage /> }],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: paths.dashboard, element: <DashboardPage /> },
          { path: paths.contracts, element: <ContractsPage /> },
          { path: paths.contractNew, element: <ContractNewPage /> },
          { path: "/contracts/:contractId", element: <ContractDetailPage /> },
          {
            path: "/contracts/:contractId/edit",
            element: <ContractEditPage />,
          },
          {
            path: "/contracts/:contractId/orders/new",
            element: <OrderNewPage />,
          },
          { path: paths.orders, element: <OrdersPage /> },
          { path: "/orders/:orderId", element: <OrderDetailPage /> },
          { path: "/orders/:orderId/edit", element: <OrderEditPage /> },
          { path: paths.reception, element: <IntakePage /> },
          { path: "/reception/:loadId", element: <IntakeDetailPage /> },
          {
            path: "/delivery-forms/:deliveryFormId",
            element: <DeliveryFormDetailPage />,
          },
          { path: paths.millFloor, element: <MillFloorPage /> },
          { path: "/mill-floor/:usinageId", element: <UsinageDetailPage /> },
          { path: paths.lots, element: <LotsPage /> },
          { path: "/lots/:lotId", element: <LotDetailPage /> },
          { path: paths.transitOrders, element: <TransitOrdersPage /> },
          { path: "/transit-orders/:otId", element: <TransitDetailPage /> },
          {
            path: "/transit-orders/:otId/traceability",
            element: <TransitTraceabilityPage />,
          },
          {
            path: paths.clients,
            element: <Placeholder titleKey="items.clients" />,
          },
          { path: paths.cooperatives, element: <CooperativesPage /> },
          {
            path: "/cooperatives/:cooperativeId",
            element: <CooperativeDetailPage />,
          },
          {
            path: paths.usersRoles,
            element: <Placeholder titleKey="items.usersRoles" />,
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
])
